// Authenticated layout shell: sidebar nav + main content area.

import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Activity,
  Award,
  Briefcase,
  ClipboardList,
  Coins,
  CreditCard,
  Dumbbell,
  Landmark,
  LayoutDashboard,
  LineChart,
  LogOut,
  Menu,
  MessageSquare,
  KeyRound,
  Newspaper,
  Settings2,
  Shield,
  Skull,
  Store,
  TrendingUp,
  Users,
} from 'lucide-react';
import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { Button, cn } from '@awakenedlands/ui';

interface NavLink {
  to: string;
  label: string;
  icon: React.ReactNode;
}

interface NavSection {
  /** Section heading shown above the links. Null = no heading (top-level). */
  heading: string | null;
  links: NavLink[];
}

// Nav is organized into sections. The "Economy" section is the home for
// economy-observability dashboards (epic #550). Add future economy dashboards
// (#559, #561–#566) by appending to that section's links array.
const NAV_SECTIONS: NavSection[] = [
  {
    heading: null,
    links: [
      { to: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard size={16} /> },
      { to: '/users', label: 'Players', icon: <Users size={16} /> },
      { to: '/audit', label: 'Audit Log', icon: <ClipboardList size={16} /> },
      { to: '/ip-bans', label: 'IP Bans', icon: <Shield size={16} /> },
      { to: '/support-inbox', label: 'Support', icon: <MessageSquare size={16} /> },
      { to: '/cron-health', label: 'Cron Health', icon: <Activity size={16} /> },
      { to: '/staff', label: 'Staff Accounts', icon: <Users size={16} /> },
      { to: '/settings/webauthn', label: 'Passkeys', icon: <KeyRound size={16} /> },
    ],
  },
  {
    heading: 'Economy',
    links: [
      { to: '/economy-summary', label: 'Overview', icon: <LineChart size={16} /> },
      { to: '/casino-metrics', label: 'Casino Metrics', icon: <TrendingUp size={16} /> },
      { to: '/banking-metrics', label: 'Banking', icon: <Landmark size={16} /> },
      { to: '/crime-metrics', label: 'Crimes', icon: <Skull size={16} /> },
      { to: '/gym-metrics', label: 'Gym', icon: <Dumbbell size={16} /> },
      { to: '/career-metrics', label: 'Careers', icon: <Briefcase size={16} /> },
      { to: '/market-metrics', label: 'Market', icon: <Store size={16} /> },
      { to: '/player-economy-metrics', label: 'Player Economy', icon: <Coins size={16} /> },
      { to: '/xp-metrics', label: 'XP Sources', icon: <Award size={16} /> },
    ],
  },
  {
    heading: 'Content & Config',
    links: [
      { to: '/config', label: 'Config Editor', icon: <Settings2 size={16} /> },
      { to: '/newspaper', label: 'Newspaper CMS', icon: <Newspaper size={16} /> },
      { to: '/payments', label: 'Payments', icon: <CreditCard size={16} /> },
    ],
  },
];

function Sidebar({ onClose }: { onClose?: () => void }) {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  async function handleLogout() {
    await apiFetch('/admin/auth/logout', { method: 'POST' });
    queryClient.removeQueries({ queryKey: ['staff-me'] });
    void navigate({ to: '/login' });
  }

  return (
    <nav className="flex h-full flex-col bg-panel text-ink-high">
      {/* Logo / heading */}
      <div className="flex items-center justify-between px-5 py-5 border-b border-divider">
        <span className="text-sm font-semibold tracking-tight font-mono text-primary">
          Awakened Lands · Admin
        </span>
        {onClose && (
          <button
            onClick={onClose}
            className="text-ink-low hover:text-ink-high md:hidden"
            aria-label="Close sidebar"
          >
            ×
          </button>
        )}
      </div>

      {/* Nav links */}
      <div className="flex-1 space-y-4 px-3 py-3" data-testid="nav-sections">
        {NAV_SECTIONS.map((section, sIdx) => (
          <div key={sIdx}>
            {section.heading !== null && (
              <p
                className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-ink-low"
                data-testid={`nav-section-${section.heading.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {section.heading}
              </p>
            )}
            <ul className="space-y-1">
              {section.links.map((link) => {
                const isActive = currentPath === link.to;
                return (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      onClick={onClose}
                      className={cn(
                        'flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors',
                        isActive
                          ? 'bg-primary-soft text-primary'
                          : 'text-ink-mid hover:bg-panel-elev hover:text-ink-high',
                      )}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {link.icon}
                      {link.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {/* Logout */}
      <div className="p-4 border-t border-divider">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-ink-mid hover:bg-panel-elev hover:text-ink-high"
          onClick={() => void handleLogout()}
          data-testid="logout-button"
        >
          <LogOut size={16} />
          Sign out
        </Button>
      </div>
    </nav>
  );
}

interface ShellProps {
  children: React.ReactNode;
}

export function Shell({ children }: ShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-dvh">
      {/* Desktop sidebar — always visible on md+ */}
      <aside className="hidden w-60 flex-shrink-0 md:block">
        <div className="fixed h-full w-60">
          <Sidebar />
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          {/* Backdrop (token scrim) */}
          <div
            className="absolute inset-0 bg-background/85"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <div className="relative z-10 w-60">
            <Sidebar onClose={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col">
        {/* Mobile top bar */}
        <header className="flex items-center gap-3 border-b border-border px-4 py-3 md:hidden bg-panel">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Open sidebar"
            className="rounded-sm p-1 hover:bg-panel-elev text-ink-mid"
          >
            <Menu size={20} />
          </button>
          <span className="text-sm font-semibold font-mono text-primary">Awakened Lands · Admin</span>
        </header>

        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}
